import React from 'react';
import { Button } from '../ui/button';
import { Sparkles, MessageCircle } from 'lucide-react';

const WHATSAPP_NUMBER = '447378415338';

const QuoteResult = ({ result, onReset }) => {
  const whatsappMessage = encodeURIComponent(
    `Hi! I just received a quote for ${result.style?.name} shutters in ${result.material?.name} (${result.installationType?.name || 'Full Service'}) - Total: £${result.totalPrice}. I'd like to discuss booking a home visit.`
  );
  const whatsappLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${whatsappMessage}`;
  
  return (
    <div className="space-y-6 text-center">
      <div className="py-4">
        <Sparkles className="w-12 h-12 mx-auto text-[#8CB89F] mb-4" />
        <h2 className="text-2xl font-bold text-gray-900">Your Dream Shutters Await!</h2>
        <p className="text-gray-500 mt-1">Here's your personalised quote, {result.customerName}</p>
      </div>
      
      {/* Price Display */}
      <div className="bg-gradient-to-br from-[#8CB89F] to-[#7aa88f] text-white rounded-2xl p-8 shadow-lg">
        <p className="text-sm opacity-90 mb-1">Your Estimate</p>
        <p className="text-5xl font-bold">£{result.totalPrice}</p>
        <p className="text-sm opacity-90 mt-2">
          {result.isDiy ? 'Inc. VAT & Delivery' : 'Inc. VAT & Installation'}
        </p>
        <p className="text-xs opacity-75 mt-1">10 Year Structural Warranty Included</p>
      </div>
      
      {/* DIY Banner */}
      {result.isDiy && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
          <p className="font-semibold text-green-800">DIY Install Selected</p>
          <p className="text-sm text-green-700 mt-1">
            We Measure, We Deliver, You Install!
          </p>
          <p className="text-xs text-green-600 mt-1">
            100% Guaranteed To Fit. Measured By Experts. Installed By You.
          </p>
        </div>
      )}
      
      {/* Price Match Message */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
        <p className="font-semibold text-amber-800">Had A Better Price?</p>
        <p className="text-sm text-amber-700 mt-1">
          We'll Always Do What We Can To Match Like For Like Quotes!
        </p>
        <p className="text-sm text-amber-600 mt-1">
          Get In Touch And Let's Get Started On Your Shutter Journey Together!
        </p>
      </div>
      
      {/* Specifications */}
      <div className="bg-gray-50 rounded-xl p-6 text-left">
        <h3 className="font-semibold text-gray-900 mb-4">Your Specifications</h3>
        
        <div className="space-y-3">
          {result.windows.map((window, index) => (
            <div key={index} className="flex justify-between text-sm">
              <span className="text-gray-600">{window.label}</span>
              <span className="font-medium">{window.width}mm × {window.height}mm</span>
            </div>
          ))}
          
          <div className="border-t pt-3 mt-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Style</span>
              <span className="font-medium">{result.style?.name}</span>
            </div>
            <div className="flex justify-between text-sm mt-2">
              <span className="text-gray-600">Material</span>
              <span className="font-medium">{result.material?.name}</span>
            </div>
            <div className="flex justify-between text-sm mt-2">
              <span className="text-gray-600">Installation</span>
              <span className="font-medium">Full Service</span>
            </div>
            <div className="flex justify-between text-sm mt-2">
              <span className="text-gray-600">Warranty</span>
              <span className="font-medium">10 Years</span>
            </div>
            <div className="flex justify-between text-sm mt-2">
              <span className="text-gray-600">Total Area</span>
              <span className="font-medium">{result.totalArea} m²</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Email confirmation */}
      <div className="bg-blue-50 rounded-lg p-4 text-sm text-blue-700">
        <p>We've sent your estimate to <strong>{result.customerEmail}</strong></p>
      </div>
      
      {/* CTA Buttons */}
      <div className="space-y-3 pt-4">
        <Button 
          className="w-full bg-[#25D366] hover:bg-[#20bd5a] text-white" 
          size="lg"
          onClick={() => window.open(whatsappLink, '_blank')}
          data-testid="whatsapp-btn"
        >
          <MessageCircle className="w-5 h-5 mr-2" />
          Book A Home Visit On WhatsApp
        </Button>
        <Button variant="outline" className="w-full" onClick={onReset} data-testid="start-new-quote-btn">
          Start New Quote
        </Button>
      </div>
      
      <p className="text-xs text-gray-400">
        This is an estimate. Final price confirmed after home survey.
      </p>
    </div>
  );
};

export default QuoteResult;
